import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useRef, useState, FC, ReactNode } from 'react';
import { Context } from '@context/context';
import { IState } from '@context/stateModels';
import { Field } from '@pages/api/services';
import { InvertStepMap, StepsMap } from '@utils/StepsMap';
import { isCrossOnboardingProcess } from '@utils/processUtils';
import StepBar from '@components/shared/StepBar/StepBar';
import StepperSidebar from '@components/shared/StepperSidebar/StepperSidebar';
import LegacyStepBar from '@components/shared/StepBar/LegacyStepBar';
import BackArrowButton from '@components/icons/BackArrowButton/BackArrowButton';
import { usePartnerConfig } from '@hooks/usePartnerConfig';
import { useBffFeatureFlags } from '@hooks/useBffFeatureFlags';
import { useCommonLabels } from '@hooks/useCommonLabels';
import { sessionView } from './utilsFormatter';
import { InterruptProcessButton, PartnerMobileHeader, PartnerDesktopLogo } from './Components';
import LinkBankingTransparency from '@components/shared/LinkBankingTransparency/LinkBankingTransparency';
import DynamicBanner from '@components/shared/DynamicBanner/DynamicBanner';

import styles from './Formatter.module.scss';
import { useNetwork } from '@hooks/useNetwork';

export type Props = {
  pages?: any;
  canSave?: () => Promise<void>;
  resetTimer?: () => void;
  showHeader?: boolean;
  /** Booleano per le pagine che azzerano il countdown una volta sola, contatore per chi lo rifà. */
  refreshTimer?: boolean | number;
  isTYP?: boolean;
  customHeight?: boolean;
  hasBackMobile?: boolean;
  children?: ReactNode;
  hasCustomHeaderMobile?: () => void;
  isProcessBeingInterrupted?: boolean;
  isUserWaiving?: boolean;
  mobileTopOffset?: number;
};

const getTypColor = (isInterrupt = false, partnerName: string) => {
  if (isInterrupt) {
    return styles.TYPinterruptColor;
  }

  return partnerName === 'BNL' ? styles.TYPbnlColor : styles.TYPcolor;
};

const normalizeClassName = (className: string) => className.replace(/\s+/g, ' ').trim();

const getPartnerName = (partnerName?: string) => partnerName ?? 'AideXa';

const getShowBnlMobileHeader = (showPartnerHeader: boolean, partnerName: string) =>
  showPartnerHeader && partnerName === 'BNL';

const getCurrentStep = (actualStep: string, stepManagerActualStep?: string) =>
  actualStep || stepManagerActualStep || '';

const getShowBnlCompactMobileHeader = (
  showBnlMobileHeader: boolean,
  currentStep: string,
  hasCustomHeaderMobile: Props['hasCustomHeaderMobile'],
  isTYP: Props['isTYP']
) => showBnlMobileHeader && currentStep === InvertStepMap.contatti && !hasCustomHeaderMobile && !isTYP;

const getInterruptColorClass = (isProcessBeingInterrupted?: boolean) =>
  isProcessBeingInterrupted ? styles.TYPinterruptColor : '';

const getTypBackgroundClass = (isTYP: Props['isTYP'], typBackgroundColor: string) => (isTYP ? typBackgroundColor : '');

const buildRootClassName = (
  isProcessBeingInterrupted: Props['isProcessBeingInterrupted'],
  customHeight: Props['customHeight'],
  isTYP: Props['isTYP'],
  typBackgroundColor: string
) => {
  const interruptColorClass = getInterruptColorClass(isProcessBeingInterrupted);
  const wrapperClassName = customHeight ? styles.totalWrapperCustom : styles.totalWrapper;
  const typBackgroundClass = isTYP && !isProcessBeingInterrupted ? typBackgroundColor : '';

  return `${interruptColorClass} ${wrapperClassName} ${typBackgroundClass}`;
};

const buildButtonsClassName = (
  showBnlCompactMobileHeader: boolean,
  interruptColorClass: string,
  typBackgroundClass: string
) => {
  const buttonsClassName = `${styles.buttons} ${interruptColorClass} ${typBackgroundClass}`;

  if (!showBnlCompactMobileHeader) {
    return buttonsClassName;
  }

  return normalizeClassName(`${buttonsClassName} ${styles.hiddenButtonsForCompactBnlMobile}`);
};

const buildPageWrapperClassName = () => styles.pageWrapper;

const buildBannerWrapperClassName = (copyShowHeader: boolean) =>
  `${styles.bannerWrapper} ${copyShowHeader ? styles.marginWrapper : ''}`;

const buildPageContentClassName = (
  showBnlCompactMobileHeader: boolean,
  showBnlMobileHeader: boolean,
  showPartnerHeader: boolean
) => {
  if (showBnlCompactMobileHeader) {
    return `${styles.pageContent} ${styles.bnlCompactPartnerMargin}`;
  }

  if (showBnlMobileHeader) {
    return `${styles.pageContent} ${styles.bnlPartnerMargin}`;
  }

  return `${styles.pageContent} ${showPartnerHeader ? styles.partnerMargin : ''}`;
};

const shouldShowBackButton = (
  actualStep: string,
  hasCustomHeaderMobile: boolean,
  isTYP: Props['isTYP'],
  hasBackMobile: boolean
) => (actualStep !== InvertStepMap.contatti || hasCustomHeaderMobile) && !isTYP && hasBackMobile;

// The #26994 stepper describes the lending macro-areas, so a bank-account-only process keeps
// the legacy progress bar even with the flag on. Cross onboarding reaches the lending steps,
// so it gets the new stepper like any other lending flow.
const isBankAccountOnlyFlow = (isConto?: boolean, processDefinition?: string) =>
  Boolean(isConto) && !isCrossOnboardingProcess(processDefinition);

// A bank account process only knows whether it is a cross onboarding one once /steps answers with
// the process definition (UPDATE_STEP_LIST). Until then no bar is painted at all, so the user
// never sees the legacy bar swapped for the new stepper mid-flow.
const isBankAccountFlowUnknown = (isConto?: boolean, processDefinition?: string) =>
  Boolean(isConto) && !processDefinition;

// Feature flag Onboarding.All.Stepbar: true = #26994 stepper, false = legacy progress
// bar, undefined = flag not known yet, so nothing is painted.
const shouldShowStepper = (isTYP: Props['isTYP'], bankAccountOnly: boolean, stepBarEnabled?: boolean) =>
  !isTYP && !bankAccountOnly && stepBarEnabled === true;

// La sidebar (>=1440px) e la barra orizzontale compatta sono visibili entrambe fin dal
// primo step (contatti) — coerenti tra loro, stessa condizione (!isTYP).
const shouldShowStepperSidebar = (isTYP: Props['isTYP'], bankAccountOnly: boolean, stepBarEnabled?: boolean) =>
  !isTYP && !bankAccountOnly && stepBarEnabled === true;

// Pre-#26994 behaviour: thin bar, hidden on the first step (contatti). Also the bank-account-only
// behaviour with the flag on.
const shouldShowLegacyStepper = (
  actualStep: string,
  isTYP: Props['isTYP'],
  bankAccountOnly: boolean,
  bankAccountFlowUnknown: boolean,
  stepBarEnabled?: boolean
) =>
  (stepBarEnabled === false || (bankAccountOnly && !bankAccountFlowUnknown && stepBarEnabled === true)) &&
  actualStep !== InvertStepMap.contatti &&
  !isTYP;

const shouldShowActualStepLogo = (actualStep: string, hasCustomHeaderMobile: boolean, isTYP: Props['isTYP']) =>
  (actualStep === InvertStepMap.contatti && !hasCustomHeaderMobile) || Boolean(isTYP);

/** Used until /featureFlags answers with the value configured for the environment. */
const SESSION_TIMEOUT_FALLBACK_SECONDS = 180;

const shouldShowSessionView = (timer: boolean, showSessionExpired: boolean, showLoader: boolean) =>
  (timer || showSessionExpired) && !showLoader;

// #26994: --mobile-top-offset vive sul wrapper di pagina e non piu' sul solo banner, perche'
// serve a due elementi: il banner ci calcola il proprio margine (.marginWrapper, applicato solo
// con l'header visibile) e lo stepper mobile ci si aggancia da sticky, sotto l'header fisso.
// Per questo non e' piu' subordinata a copyShowHeader: l'header e' fisso in ogni caso.
const getMobileTopOffsetStyle = (mobileTopOffset?: number, measuredHeaderHeight?: number) => {
  const offset = mobileTopOffset ?? measuredHeaderHeight;

  return offset ? ({ ['--mobile-top-offset']: `${offset}px` } as any) : undefined;
};

const redirectToContactsIfMissingBpmId = (actualStep: string | undefined, router: ReturnType<typeof useRouter>) => {
  if (actualStep === InvertStepMap.contatti) {
    return;
  }

  const bpmId = sessionStorage.getItem('bpmId');

  if (!bpmId) {
    router.push('../contatti');
  }
};

const refreshSessionTimer = (
  refreshTimer: Props['refreshTimer'],
  sessionTimeout: number,
  resetTimer: Props['resetTimer'],
  setCount: (value: number) => void
) => {
  if (!refreshTimer) {
    return;
  }

  setCount(sessionTimeout);
  resetTimer?.();
};

const updateSessionTimerCount = (
  prevCount: number,
  sessionTimeout: number,
  setTimer: (value: boolean) => void,
  setTimerFinish: (value: boolean) => void
) => {
  if (prevCount === 31) {
    setTimer(true);
  }

  if (prevCount > 0 && prevCount <= sessionTimeout) {
    return prevCount - 1;
  }

  setTimerFinish(true);
  return prevCount;
};

const syncSessionState = (
  actualStep: string | undefined,
  shouldShowSessionExpired: boolean,
  setActualStep: (value: string) => void,
  setShowSessionExpired: (value: boolean) => void
) => {
  setActualStep(actualStep ?? '');

  if (shouldShowSessionExpired) {
    setShowSessionExpired(true);
  }
};

const handleGoBack = (
  setCopyShowHeader: (value: boolean) => void,
  hasCustomHeaderMobile: Props['hasCustomHeaderMobile'],
  instructionForBack: IState['state']['instructionForBack'],
  back: () => Promise<void>
) => {
  setCopyShowHeader(true);

  if (hasCustomHeaderMobile) {
    hasCustomHeaderMobile();
    return;
  }

  if (instructionForBack != null) {
    instructionForBack();
    return;
  }

  void back();
};

const redirectToPublicSite = () => {
  const { hostname, origin } = window.location;
  window.location.assign(hostname === 'localhost' ? `${origin}/onboarding/dinamico/contatti` : origin);
};

const Formatter: FC<Props> = ({
  pages,
  canSave,
  showHeader = true,
  refreshTimer,
  isTYP,
  customHeight,
  resetTimer,
  hasBackMobile = true,
  children,
  hasCustomHeaderMobile = undefined,
  isProcessBeingInterrupted,
  mobileTopOffset
}) => {
  const { state } = useContext<IState>(Context);
  const { stepBarEnabled, sessionTimeoutSeconds } = useBffFeatureFlags(false);
  const router = useRouter();
  const headerRef = useRef<HTMLDivElement>(null);
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState<number>();
  const [copyShowHeader, setCopyShowHeader] = useState(showHeader);
  const [timer, setTimer] = useState(false);
  const [timerFinish, setTimerFinish] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  // Session timeout in seconds. The BFF owns the value per environment (`/featureFlags`) because
  // the bundle is built once for every environment: the env variable stays as the local fallback,
  // and the baseline is 3 minutes on the page.
  const sessionTimeout =
    sessionTimeoutSeconds ??
    (Number(process.env.NEXT_PUBLIC_SESSION_TIMEOUT_SECONDS) || SESSION_TIMEOUT_FALLBACK_SECONDS);
  const [count, setCount] = useState(sessionTimeout);
  const [actualStep, setActualStep] = useState('');
  const { partnerConfig } = usePartnerConfig();
  const { sessionExpiredView: sessionSubscription, linkBankingTransparency } = useCommonLabels();

  const isContactsStep = useMemo(() => {
    return state.stepManager.actualStep === InvertStepMap.contatti;
  }, [state]);

  const showPartnerHeader = useMemo(() => !!partnerConfig?.partnerName, [partnerConfig]);
  const partnerName = getPartnerName(partnerConfig?.partnerName);
  const showBnlMobileHeader = getShowBnlMobileHeader(showPartnerHeader, partnerName);
  const currentStep = getCurrentStep(actualStep, state?.stepManager?.actualStep);
  const showBnlCompactMobileHeader = getShowBnlCompactMobileHeader(
    showBnlMobileHeader,
    currentStep,
    hasCustomHeaderMobile,
    isTYP
  );

  const typBackgroundColor = getTypColor(state.coreInformation.interruptProcess, partnerName);
  const interruptColorClass = getInterruptColorClass(isProcessBeingInterrupted);
  const typBackgroundClass = getTypBackgroundClass(isTYP, typBackgroundColor);
  const rootClassName = buildRootClassName(isProcessBeingInterrupted, customHeight, isTYP, typBackgroundColor);
  const buttonsClassName = buildButtonsClassName(showBnlCompactMobileHeader, interruptColorClass, typBackgroundClass);
  const pageWrapperClassName = buildPageWrapperClassName();
  const bannerWrapperClassName = buildBannerWrapperClassName(copyShowHeader);
  const pageContentClassName = buildPageContentClassName(
    showBnlCompactMobileHeader,
    showBnlMobileHeader,
    showPartnerHeader
  );
  const showBackButton = shouldShowBackButton(currentStep, Boolean(hasCustomHeaderMobile), isTYP, hasBackMobile);
  const bankAccountOnly = isBankAccountOnlyFlow(state.coreInformation?.isConto, state.stepManager?.processDefinition);
  const bankAccountFlowUnknown = isBankAccountFlowUnknown(
    state.coreInformation?.isConto,
    state.stepManager?.processDefinition
  );
  const showStepper = shouldShowStepper(isTYP, bankAccountOnly, stepBarEnabled);
  const showStepperSidebar = shouldShowStepperSidebar(isTYP, bankAccountOnly, stepBarEnabled);
  const showLegacyStepper = shouldShowLegacyStepper(
    currentStep,
    isTYP,
    bankAccountOnly,
    bankAccountFlowUnknown,
    stepBarEnabled
  );
  const showActualStepLogo = shouldShowActualStepLogo(currentStep, Boolean(hasCustomHeaderMobile), isTYP);
  // #26994: con lo stepper attivo lo stacco sopra il titolo di pagina lo dichiara il Formatter
  // (vedi .stepperLayout in Formatter.module.scss). Concatenata solo quando serve, cosi' la
  // classe del wrapper resta identica a prima con lo stepper spento.
  const stepperLayoutClass = showStepper || showStepperSidebar ? ` ${styles.stepperLayout}` : '';
  const showSessionExpiredView = shouldShowSessionView(timer, showSessionExpired, state.showLoader);
  const mobileTopOffsetStyle = getMobileTopOffsetStyle(mobileTopOffset, measuredHeaderHeight);

  useEffect(() => {
    const headerElement = headerRef.current;

    if (!headerElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setMeasuredHeaderHeight(entry.contentRect.height);
    });

    observer.observe(headerElement);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    redirectToContactsIfMissingBpmId(state.stepManager?.actualStep, router);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshSessionTimer(refreshTimer, sessionTimeout, resetTimer, setCount);
  }, [refreshTimer, resetTimer, sessionTimeout]);

  // The configured value lands after the first render: restart the countdown on it, otherwise the
  // page would keep counting down from the fallback.
  useEffect(() => {
    if (sessionTimeoutSeconds === undefined) {
      return;
    }

    setCount(sessionTimeoutSeconds);
  }, [sessionTimeoutSeconds]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prevCount => updateSessionTimerCount(prevCount, sessionTimeout, setTimer, setTimerFinish));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [sessionTimeout]);

  useEffect(() => {
    setCopyShowHeader(showHeader);
  }, [showHeader]);

  const { fireRequest: backResponse } = useNetwork({
    url: '/back',
    field: Field.businessdata,
    method: 'post',
    coreInformation: state.coreInformation
  });

  const back = async () => {
    try {
      const body = JSON.stringify({});
      const response = await backResponse({ body });
      router.push(`/${StepsMap[response.payload.next.name]}`);
    } catch (err) {
      console.error(err);
    }
  };

  const goBack = () => handleGoBack(setCopyShowHeader, hasCustomHeaderMobile, state.instructionForBack, back);

  useEffect(() => {
    syncSessionState(state?.stepManager?.actualStep, state.showSessionExpired, setActualStep, setShowSessionExpired);
  }, [state.showSessionExpired, state?.stepManager?.actualStep]);

  if (showSessionExpiredView) {
    return sessionView(timerFinish, count, sessionSubscription, showSessionExpired);
  }
  return (
    <div className={`${rootClassName}${stepperLayoutClass}`} style={mobileTopOffsetStyle}>
      {showStepperSidebar && <StepperSidebar />}
      <div className={styles.header} ref={headerRef}>
        <PartnerMobileHeader
          partnerName={partnerName}
          showPartnerHeader={showPartnerHeader}
          rightContent={
            showBnlCompactMobileHeader ? (
              <InterruptProcessButton
                isContactsStep={isContactsStep}
                redirectToPublicSite={redirectToPublicSite}
                saveProcessData={canSave}
              />
            ) : undefined
          }
        />

        <section className={buttonsClassName}>
          <div className={styles.backWrapper}>
            {showBackButton && (
              <>
                <div className={styles.backButton}>
                  <BackArrowButton onClick={goBack} />
                  <div className={styles.linkTransparencyArrow}>
                    <LinkBankingTransparency partnerName={partnerName} link={linkBankingTransparency} />
                  </div>
                </div>
              </>
            )}
          </div>
          <div
            className={`${styles.logoWrapper}
                    ${showPartnerHeader ? styles.hiddenForPartner : ''}
                    ${showActualStepLogo ? styles.actualStepLogo : ''}
                    ${partnerName === 'BNL' ? styles.bnlLogoWrapper : ''}
                  `}
          >
            <div>
              <PartnerDesktopLogo
                partnerName={partnerName}
                showPartnerHeader={showPartnerHeader}
                logoUrl={partnerConfig?.logoUrl}
              />
              <div className={styles.linkTransparencyLogo}>
                <LinkBankingTransparency partnerName={partnerName} link={linkBankingTransparency} isLogoContext />
              </div>
            </div>
          </div>
          {showLegacyStepper && (
            <div className={styles.legacyStepperWrapper}>
              <LegacyStepBar />
            </div>
          )}
          <div className={`${styles.closerWrapper} ${isTYP ? styles.typDisable : ''}`}>
            <div className={styles.closingButton}>
              <InterruptProcessButton
                isContactsStep={isContactsStep}
                redirectToPublicSite={redirectToPublicSite}
                saveProcessData={canSave}
              />
            </div>
          </div>
        </section>
      </div>

      <div className={bannerWrapperClassName}>
        <DynamicBanner />
      </div>

      {showStepper && (
        <div className={styles.stepperWrapper}>
          <StepBar />
        </div>
      )}

      <section className={pageWrapperClassName}>
        <div className={pageContentClassName}>
          {pages && pages()}
          {children}
        </div>
      </section>
    </div>
  );
};

export default Formatter;
